import React from 'react';
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { 
  Bot, 
  MessageSquare, 
  Zap, 
  Clock, 
  TrendingUp, 
  Users, 
  CheckCircle2, 
  XCircle, 
  ArrowRight,
  Phone,
  LayoutDashboard
} from "lucide-react";
import { cn } from "@/lib/utils";

export default function DigitalEcosystemPage() {
  return (
    <div className="min-h-screen bg-parchment">
      <Navbar />
      <main className="pt-20">
        {/* 1. HERO SECTION */}
        <section className="py-24 bg-ink text-parchment border-b-2 border-ink relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 grain-texture pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl">
              <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Estructura de Alto Rendimiento</span>
              <h1 className="text-6xl md:text-8xl font-display font-black uppercase italic leading-none mb-8">
                Inteligencia <br /> <span className="text-terracotta text-stroke-parchment">Operativa 360°</span>
              </h1>
              <p className="text-2xl font-serif italic opacity-60 leading-relaxed max-w-2xl mb-12">
                Elevamos la gestión de tu corretaje mediante una infraestructura que unifica comunicaciones, CRM especializado y agentes de IA diseñados para la conversión.
              </p>
              <div className="flex gap-4">
                <Button variant="terracotta" size="lg" className="h-16 px-12 text-lg">SOLICITAR CONSULTORÍA</Button>
                <Button variant="outline" size="lg" className="h-16 px-12 text-lg border-parchment text-parchment hover:bg-parchment hover:text-ink">ESTRUCTURA TÉCNICA</Button>
              </div>
            </div>
          </div>
        </section>

        {/* 2. PAIN POINTS / RESOLUTION */}
        <section className="py-32 bg-parchment">
          <div className="container mx-auto px-4">
            <div className="text-center mb-24">
              <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic mb-6 text-ink">Eficiencia sin Fricciones</h2>
              <p className="text-xl font-serif italic text-ink/60 max-w-2xl mx-auto">Optimización • Velocidad • Escalabilidad</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
              {[
                { 
                  title: "Velocidad de Respuesta", 
                  stat: "90%", 
                  context: "de efectividad se pierde en los primeros 5 minutos de espera.",
                  solution: "Atención inmediata y personalizada vía WhatsApp con disponibilidad absoluta.",
                  icon: Clock
                },
                { 
                  title: "Seguimiento Metódico", 
                  stat: "30%", 
                  context: "del potencial de cierre se desvanece por falta de persistencia técnica.",
                  solution: "Secuencias de contacto automatizadas y agendamiento inteligente sin errores.",
                  icon: TrendingUp
                },
                { 
                  title: "Costo de Operación", 
                  stat: "85%", 
                  context: "de reducción en costos fijos comparado con estructuras tradicionales.",
                  solution: "Infraestructura IA que procesa volúmenes masivos con precisión editorial.",
                  icon: Users
                }
              ].map((card, i) => (
                <div key={i} className="brutalist-border bg-ink text-parchment p-12 flex flex-col gap-8 hover:bg-terracotta transition-colors group">
                  <div className="flex items-center gap-4">
                    <card.icon className="h-8 w-8 text-terracotta group-hover:text-ink" />
                    <h3 className="text-xl font-display font-black uppercase italic">{card.title}</h3>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-5xl font-display font-black">{card.stat}</span>
                      <span className="text-xs font-serif italic opacity-60">{card.context}</span>
                    </div>
                    <div className="bg-parchment/10 p-6 brutalist-border-sm mt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle2 className="h-4 w-4 text-terracotta group-hover:text-ink" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Enfoque LATAM</span>
                      </div>
                      <p className="text-sm font-serif italic">{card.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 3. CORE FEATURES GRID */}
        <section className="py-32 bg-ink text-parchment relative overflow-hidden">
          <div className="absolute inset-0 opacity-5 grain-texture pointer-events-none" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-24">
              <span className="text-xs font-black uppercase tracking-[0.5em] text-terracotta mb-6 block">Ingeniería para el Corredor</span>
              <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic text-parchment">Arquitectura del Éxito</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="space-y-8">
                <div className="flex gap-4 items-center">
                  <div className="p-4 bg-terracotta text-parchment rotate-3 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]">
                    <MessageSquare className="h-8 w-8" />
                  </div>
                  <h3 className="text-3xl font-display font-black uppercase italic">Comunicaciones</h3>
                </div>
                <p className="font-serif italic text-parchment/60 leading-relaxed">Centralización técnica de canales digitales con trazabilidad total y auditoría de calidad en tiempo real.</p>
                <ul className="space-y-4">
                  {["Interfáz de WhatsApp Business", "Trazabilidad de comunicaciones", "Análisis de calidad operativa"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm font-black uppercase tracking-widest">
                      <CheckCircle2 className="h-4 w-4 text-terracotta" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-8">
                <div className="flex gap-4 items-center">
                  <div className="p-4 bg-ink text-terracotta border border-terracotta -rotate-3 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]">
                    <LayoutDashboard className="h-8 w-8" />
                  </div>
                  <h3 className="text-3xl font-display font-black uppercase italic">Gestión Activa</h3>
                </div>
                <p className="font-serif italic text-parchment/60 leading-relaxed">Pipeline de transacciones optimizado para el ciclo de vida inmobiliario, desde la prospección hasta el cierre.</p>
                <ul className="space-y-4">
                  {["Segmentación inteligente", "Embudos de alta conversión", "Control administrativo central"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm font-black uppercase tracking-widest">
                      <CheckCircle2 className="h-4 w-4 text-terracotta" /> {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-8">
                <div className="flex gap-4 items-center">
                  <div className="p-4 bg-parchment text-ink rotate-6 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.1)]">
                    <Bot className="h-8 w-8" />
                  </div>
                  <h3 className="text-3xl font-display font-black uppercase italic">Agentes IA</h3>
                </div>
                <p className="font-serif italic text-parchment/60 leading-relaxed">Modelos de IA entrenados en el mercado local para calificar prospectos y gestionar agendas sin intervención humana.</p>
                <ul className="space-y-4">
                  {["Calificación por perfil financiero", "Filtro de requerimientos", "Coordinación autónoma de visitas"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm font-black uppercase tracking-widest">
                      <CheckCircle2 className="h-4 w-4 text-terracotta" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 5. 3-STEP ONBOARDING */}
        <section className="py-32 bg-parchment border-t-2 border-ink">
          <div className="container mx-auto px-4">
            <div className="text-center mb-24">
              <h2 className="text-5xl md:text-7xl font-display font-black uppercase italic mb-6 text-ink">Protocolo de Activación</h2>
              <p className="text-xl font-serif italic text-ink/60">Infraestructura digital lista en tiempo récord.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              <div className="absolute top-1/2 left-0 w-full h-[2px] bg-ink/10 hidden md:block -z-0" />
              {[
                { step: "1", title: "Configuración Técnica", desc: "Definición de canales y estructura de embudos transaccionales.", icon: Zap },
                { step: "2", title: "Entrenamiento IA", desc: "Integración de guiones comerciales y parámetros de calidad.", icon: Bot },
                { step: "3", title: "Despliegue Operativo", desc: "Activación de la infraestructura con supervisión continua.", icon: Zap }
              ].map((item, i) => (
                <div key={i} className="brutalist-border bg-ink p-12 relative z-10 hover:bg-terracotta transition-colors group">
                  <div className="flex justify-between items-start mb-12 text-parchment">
                    <div className="bg-terracotta p-4 group-hover:bg-ink transition-colors">
                      <item.icon className="h-8 w-8 text-parchment" />
                    </div>
                    <span className="text-6xl font-display font-black opacity-10 group-hover:opacity-30">{item.step}</span>
                  </div>
                  <h3 className="text-2xl font-display font-black uppercase italic mb-4 text-parchment">{item.title}</h3>
                  <p className="font-serif italic text-parchment/60 group-hover:text-parchment">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-24 text-center">
              <Button variant="terracotta" size="lg" className="h-20 px-16 text-xl group">
                INICIAR DESPLIEGUE <ArrowRight className="ml-4 h-6 w-6 group-hover:translate-x-2 transition-transform" />
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
